import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import { LuMapPinOff } from 'react-icons/lu';
import {
  parseLatLng,
  splitByLocation,
  computeBoundsStrategy,
  coordSignature,
  FALLBACK_CENTER as PORTED_FALLBACK_CENTER,
  FALLBACK_ZOOM as PORTED_FALLBACK_ZOOM,
} from '../utils/mapStatus.js';
import { SHARED_MAP_CONTROLS, initialMapType, writeStoredMapType } from '../../../lib/mapControls.js';
import { buildMarkerIcon } from '../utils/chargerMarkerIcon.js';
import StationPhoto from '../../../components/StationPhoto/StationPhoto.jsx';
import { resolveStationPhotoUrl } from '../../../utils/stationPhotos.js';
import './ChargerNetworkMap.less';

// Env overrides win over the ported Admin defaults so a per-deployment
// fallback can point at a different island/country if needed.
const FALLBACK_CENTER = {
  lat: Number(import.meta.env.VITE_MAP_FALLBACK_LAT) || PORTED_FALLBACK_CENTER.lat,
  lng: Number(import.meta.env.VITE_MAP_FALLBACK_LNG) || PORTED_FALLBACK_CENTER.lng,
};
const FALLBACK_ZOOM = Number(import.meta.env.VITE_MAP_FALLBACK_ZOOM) || PORTED_FALLBACK_ZOOM;

export default function ChargerNetworkMap({ chargers, onOpenCharger, selectedId = null }) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'gc-network-maps',
    googleMapsApiKey: apiKey,
  });

  // Filter to chargers we can pin. `splitByLocation` uses the same
  // `parseLatLng` guard as the Admin app (rejects NaN / Infinity /
  // null-island), so the two apps agree on what "has coordinates"
  // means.
  const { pinned } = useMemo(() => splitByLocation(chargers), [chargers]);
  const signature = useMemo(() => coordSignature(pinned), [pinned]);

  const mapRef = useRef(null);
  const [activeId, setActiveId] = useState(null);

  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Apply the ported bounds-strategy any time the marker set actually
  // changes (`signature` collapses unrelated re-renders). This is the
  // exact effect shape the Admin `MapAutoFit` component uses.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;

    const coords = pinned.map((p) => parseLatLng(p)).filter(Boolean);
    const strategy = computeBoundsStrategy(coords);

    if (strategy.mode === 'fallback') {
      map.setCenter(FALLBACK_CENTER);
      map.setZoom(FALLBACK_ZOOM);
      return;
    }
    if (strategy.mode === 'single') {
      map.setCenter(strategy.center);
      map.setZoom(strategy.zoom);
      return;
    }
    // 'fit' — build a LatLngBounds and let Google fit + pad, then cap
    // the zoom on the first idle so tightly clustered pins don't slam
    // the viewport into a street-level view.
    const bounds = new window.google.maps.LatLngBounds(
      { lat: strategy.bounds.south, lng: strategy.bounds.west },
      { lat: strategy.bounds.north, lng: strategy.bounds.east },
    );
    map.fitBounds(bounds, 48);
    const listener = window.google.maps.event.addListenerOnce(
      map,
      'idle',
      () => {
        const z = map.getZoom?.();
        if (typeof z === 'number' && z > strategy.maxZoom) {
          map.setZoom(strategy.maxZoom);
        }
      },
    );
    return () => {
      if (listener && window.google.maps.event?.removeListener) {
        window.google.maps.event.removeListener(listener);
      }
    };
    // We deliberately depend on the signature (stable per coord set),
    // not on `pinned` directly, so unrelated re-renders don't refit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Externally selected charger (Find nearest charger): open its InfoWindow
  // and bring it into view.
  useEffect(() => {
    if (!selectedId) return;
    const target = pinned.find((c) => (c.stationId || c.chargerCode) === selectedId);
    if (!target) return;
    setActiveId(selectedId);
    const ll = parseLatLng(target);
    if (ll && mapRef.current?.panTo) mapRef.current.panTo(ll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Close the InfoWindow if the active charger drops out of the set.
  useEffect(() => {
    if (activeId && !pinned.some((c) => (c.stationId || c.chargerCode) === activeId)) {
      setActiveId(null);
    }
  }, [pinned, activeId]);

  if (!apiKey || loadError) {
    return (
      <div className="gc-network-map gc-network-map--notice" role="note">
        <LuMapPinOff aria-hidden="true" />
        <span>Map is unavailable right now. Use the list below to find a charger.</span>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="gc-network-map gc-network-map--loading" aria-busy="true" aria-live="polite">
        <div className="gc-network-map-skeleton" />
      </div>
    );
  }

  if (pinned.length === 0) {
    return (
      <div className="gc-network-map gc-network-map--empty" role="note">
        <LuMapPinOff aria-hidden="true" />
        <span>No charger locations to show on the map.</span>
      </div>
    );
  }

  const active = activeId
    ? pinned.find((c) => (c.stationId || c.chargerCode) === activeId)
    : null;

  return (
    <div className="gc-network-map">
      <GoogleMap
        mapContainerClassName="gc-network-map-canvas"
        center={FALLBACK_CENTER}
        zoom={FALLBACK_ZOOM}
        onLoad={onLoadMap}
        onUnmount={onUnmount}
        mapTypeId={initialMapType('roadmap')}
        onMapTypeIdChanged={() => {
          const m = mapRef.current;
          if (m && typeof m.getMapTypeId === 'function') writeStoredMapType(m.getMapTypeId());
        }}
        options={{
          streetViewControl: false,
          ...SHARED_MAP_CONTROLS,
          clickableIcons: false,
        }}
      >
        {pinned.map((c) => {
          const coords = parseLatLng(c);
          if (!coords) return null;
          const id = c.stationId || c.chargerCode;
          // Canonical GreenCharge charger pin — no default red Google
          // marker. Priority resolution (maintenance > faulted > in-
          // use > unavailable > available) lives in the shared helper;
          // we hand the whole station shape so the resolver can
          // consult the maintenance projection AND the raw OCPP
          // status. Icon config is memoised per (statusClass, selected)
          // in the helper — a station whose bucket hasn't changed
          // between renders keeps the same object identity.
          //
          // `buildMarkerIcon` returns undefined when google.maps isn't
          // ready — `<MarkerF icon={undefined} />` falls back to the
          // default red pin during the SDK-loading gap; the next
          // render (once `isLoaded` flips true) swaps in the real
          // pin without remounting.
          const icon = buildMarkerIcon(window.google, {
            station: c,
            selected: id === activeId,
          });
          const title = c.statusLabel
            ? `${c.displayName || c.chargerCode || 'Charger'} — ${c.statusLabel}`
            : (c.displayName || c.chargerCode || 'Charger');
          return (
            <MarkerF
              key={id}
              position={coords}
              title={title}
              icon={icon}
              onClick={() => setActiveId(id)}
            />
          );
        })}
        {active && window.google?.maps?.Size && (
          <InfoWindowF
            position={parseLatLng(active)}
            onCloseClick={() => setActiveId(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -32) }}
          >
            <div className="gc-network-map-info">
              <StationPhoto
                photoUrl={resolveStationPhotoUrl(active)}
                variant="card"
                alt={active.displayName || 'Charging station'}
                className="gc-network-map-info-photo"
              />
              <p className="gc-network-map-info-title">
                {active.displayName || active.chargerCode || 'Charger'}
              </p>
              {active.chargerCode && (
                <p className="gc-network-map-info-code">Code {active.chargerCode}</p>
              )}
              {active.statusLabel && (
                <p
                  className="gc-network-map-info-status"
                  data-tone={active.statusTone || 'neutral'}
                >
                  {active.statusLabel}
                </p>
              )}
              {onOpenCharger && (
                // Even for a maintenance-flagged charger this CTA is
                // safe — the customer navigates into the charger
                // profile where the EvProfile maintenance gate
                // renders the blocked screen (no start controls
                // mount). The InfoWindow status pill above already
                // sets the right expectation.
                <button
                  type="button"
                  className="gc-network-map-info-cta"
                  onClick={() => {
                    onOpenCharger(active);
                    setActiveId(null);
                  }}
                >
                  View charger
                </button>
              )}
            </div>
          </InfoWindowF>
        )}
      </GoogleMap>
    </div>
  );
}
